import React, { useEffect, useState, useLayoutEffect } from 'react';
import { useUserStore } from '../store/userStore';
import { useToast } from '../contexts/ToastContext';
import { useProgress } from '../components/TopProgressBar';
import { companyService, Company } from '../services/companyService';
import { conversationStatusService, ConversationStatus, ConversationWithStatus } from '../services/conversationStatusService';
import { conversationService } from '../services/conversationService';
import { userService, User } from '../services/userService';
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import DraggableConversationCard from '../components/DraggableConversationCard';
import DroppableColumn from '../components/DroppableColumn';
import Button from '../components/Button';
import CustomSelect from '../components/CustomSelect';

interface Conversation {
  id: string;
  pageId: string;
  senderId: string;
  phoneNumber: string;
  participantName?: string;
  lastMessage: string;
  lastMessageTime: string;
  status: string;
  statusId?: string;
  unreadCount?: number;
  crmIntegrated?: boolean;
  updatedAt?: number;
  assignedAdvisorId?: string;
  assignedAdvisorName?: string;
}

const ConversationBoardPage: React.FC = () => {
  const { user, canAccessCompany, canAccessConversation } = useUserStore();
  const toast = useToast();
  const { start: progressStart, done: progressDone, showLoader, hideLoader } = useProgress();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [statuses, setStatuses] = useState<ConversationStatus[]>([]);
  const [conversationsByStatus, setConversationsByStatus] = useState<Record<string, Conversation[]>>({});
  const [loading, setLoading] = useState(true); // true desde el inicio para mostrar loader al montar
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [editingStatus, setEditingStatus] = useState<ConversationStatus | null>(null);
  const [statusForm, setStatusForm] = useState({
    name: '',
    color: '#3B82F6',
    isDefault: false,
  });
  const [modalErrorMessage, setModalErrorMessage] = useState<string | null>(null);
  const [openMenuStatusId, setOpenMenuStatusId] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [showStatusChangeModal, setShowStatusChangeModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterUnread, setFilterUnread] = useState(false);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [datePreset, setDatePreset] = useState<'all' | 'today' | 'week' | 'month' | 'custom'>('today');
  const [dateField, setDateField] = useState<'createdAt' | 'updatedAt'>('updatedAt');
  const [activeId, setActiveId] = useState<string | null>(null);
  const [draggedConversation, setDraggedConversation] = useState<Conversation | null>(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyData, setHistoryData] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showConversationDetail, setShowConversationDetail] = useState(false);
  const [detailConversation, setDetailConversation] = useState<Conversation | null>(null);
  const [loadingCRMId, setLoadingCRMId] = useState<string | null>(null); // id de la conv procesando CRM

  // ── Filtro de asesor ──────────────────────────────────────────────────────
  const [advisors, setAdvisors] = useState<User[]>([]);
  const [filterAdvisorId, setFilterAdvisorId] = useState<string>('all');

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Colores predefinidos para selección rápida
  const predefinedColors = [
    '#EF4444', // Red
    '#F59E0B', // Amber
    '#10B981', // Green
    '#3B82F6', // Blue
    '#8B5CF6', // Purple
    '#EC4899', // Pink
    '#6366F1', // Indigo
    '#14B8A6', // Teal
    '#F97316', // Orange
    '#84CC16', // Lime
  ];

  // Mostrar loader inmediatamente al montar (antes del primer paint)
  useLayoutEffect(() => {
    showLoader('Cargando tablero...');
    progressStart();
    return () => {
      hideLoader();
      progressDone();
    };
  }, []); // eslint-disable-line

  // Cerrar menú al hacer click fuera
  useEffect(() => {
    const handleClickOutside = () => {
      if (openMenuStatusId) {
        setOpenMenuStatusId(null);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openMenuStatusId]);

  // Cargar empresas
  useEffect(() => {
    const loadCompanies = async () => {
      try {
        const data = await companyService.getCompanies();
        let filteredCompanies = data;
        if (user && user.role !== 'administrador') {
          filteredCompanies = data.filter(company => canAccessCompany(company.configId));
        }
        setCompanies(filteredCompanies);
        if (filteredCompanies.length > 0) {
          setSelectedCompany(filteredCompanies[0]);
        } else {
          // Sin empresas asignadas — ocultar loader inmediatamente
          hideLoader();
          progressDone();
          setLoading(false);
        }
      } catch (error) {
        console.error('Error loading companies:', error);
      }
    };
    
    if (user) {
      loadCompanies();
    }
  }, [user, canAccessCompany]);

  // Cargar estados, conversaciones y asesores cuando cambia la empresa
  useEffect(() => {
    if (selectedCompany) {
      loadStatuses();
      loadConversations();
      // Cargar asesores de la empresa para el filtro
      if (user?.role !== 'asesor') {
        userService.listAdvisors(selectedCompany.configId)
          .then(list => setAdvisors(list))
          .catch(() => setAdvisors([]));
      }
      setFilterAdvisorId('all'); // Resetear filtro al cambiar empresa
    }
  }, [selectedCompany]);

  // Recargar conversaciones cuando cambien los filtros de fecha
  // REMOVIDO: Ya no se recarga automáticamente, solo con el botón Buscar
  // useEffect(() => {
  //   if (selectedCompany) {
  //     loadConversations();
  //   }
  // }, [datePreset, startDate, endDate, dateField]);

  const loadStatuses = async () => {
    if (!selectedCompany) return;
    try {
      const data = await conversationStatusService.getStatuses(selectedCompany.configId);
      setStatuses(data.sort((a, b) => a.order - b.order));
    } catch (error) {
      console.error('Error loading statuses:', error);
    }
  };

  const loadConversations = async () => {
    if (!selectedCompany) return;
    try {
      setLoading(true);
      progressStart();
      showLoader('Cargando tablero...');      const pageId = `whatsapp-${selectedCompany.phoneNumberId}`;
      
      // Calcular timestamps según el preset o fechas personalizadas
      let startTimestamp: number | undefined;
      let endTimestamp: number | undefined;
      
      if (datePreset === 'custom' && startDate && endDate) {
        // Para fechas personalizadas, parsear la fecha en zona horaria local
        // El input type="date" devuelve formato YYYY-MM-DD
        // Necesitamos parsearlo como fecha local, no UTC
        const [startYear, startMonth, startDay] = startDate.split('-').map(Number);
        const [endYear, endMonth, endDay] = endDate.split('-').map(Number);
        
        const startDateObj = new Date(startYear, startMonth - 1, startDay, 0, 0, 0, 0);
        const endDateObj = new Date(endYear, endMonth - 1, endDay, 23, 59, 59, 999);
        
        startTimestamp = startDateObj.getTime();
        endTimestamp = endDateObj.getTime();
      } else if (datePreset !== 'all') {
        const now = Date.now();
        endTimestamp = now;
        
        switch (datePreset) {
          case 'today':
            // Inicio del día de hoy en la zona horaria local
            const todayStart = new Date();
            todayStart.setHours(0, 0, 0, 0);
            startTimestamp = todayStart.getTime();
            break;
          case 'week':
            // Hace 7 días exactos
            startTimestamp = now - (7 * 24 * 60 * 60 * 1000);
            break;
          case 'month':
            // Hace 30 días exactos
            startTimestamp = now - (30 * 24 * 60 * 60 * 1000);
            break;
        }
      }
      
      const result = await conversationService.getConversations(
        pageId, 
        1000, // Límite aumentado para el tablero (con filtros de fecha es eficiente) 
        undefined, 
        startTimestamp, 
        endTimestamp,
        dateField
      );
      
      // Filtrar conversaciones según permisos del usuario (igual que en ConversationsPage)
      let filteredConversations = result.conversations;
      
      if (user) {
        if (user.role === 'asesor') {
          filteredConversations = result.conversations.filter(conv => {
            const hasAccess = canAccessConversation(conv.pageId, conv.senderId);
            return hasAccess;
          });
        }
        // operador y administrador ven todas las conversaciones de la empresa
      }
      
      // Agrupar conversaciones por estado
      const grouped: Record<string, Conversation[]> = {};
      filteredConversations.forEach((conv: Conversation) => {
        const statusId = conv.statusId || 'no-status';
        if (!grouped[statusId]) {
          grouped[statusId] = [];
        }
        grouped[statusId].push(conv);
      });
      
      setConversationsByStatus(grouped);
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setLoading(false);
      progressDone();
      hideLoader();
    }
  };

  const handleOpenStatusModal = (status?: ConversationStatus) => {
    if (status) {
      setEditingStatus(status);
      setStatusForm({
        name: status.name,
        color: status.color,
        isDefault: status.isDefault || false,
      });
    } else {
      setEditingStatus(null);
      setStatusForm({
        name: '',
        color: '#3B82F6',
        isDefault: false,
      });
    }
    setShowStatusModal(true);
  };

  const handleCloseStatusModal = () => {
    setShowStatusModal(false);
    setEditingStatus(null);
    setStatusForm({
      name: '',
      color: '#3B82F6',
      isDefault: false,
    });
    setModalErrorMessage(null);
  };

  const handleSaveStatus = async () => {
    if (!selectedCompany) return;
    
    if (!statusForm.name.trim()) {
      setModalErrorMessage('El nombre del estado es requerido');
      return;
    }

    try {
      setLoading(true);
      showLoader(editingStatus ? 'Actualizando estado...' : 'Creando estado...');
      
      if (editingStatus) {
        await conversationStatusService.updateStatus(editingStatus.statusId, statusForm);
        toast.success('Estado actualizado');
      } else {
        const newStatus = {
          configId: selectedCompany.configId,
          name: statusForm.name,
          color: statusForm.color,
          order: statuses.length,
          isDefault: statusForm.isDefault,
        };
        await conversationStatusService.createStatus(newStatus);
        toast.success('Estado creado');
      }
      
      // Recargar estados
      await loadStatuses();
      handleCloseStatusModal();
      
    } catch (error: any) {
      console.error('Error saving status:', error);
      setModalErrorMessage(error.response?.data?.error || 'Error al guardar el estado');
    } finally {
      setLoading(false);
      hideLoader();
    }
  };

  const handleDeleteStatus = async (status: ConversationStatus) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Eliminar Estado',
      message: `¿Estás seguro de eliminar el estado "${status.name}"? Esta acción no se puede deshacer.`,
      onConfirm: async () => {
        try {
          setLoading(true);
          showLoader('Eliminando estado...');
          await conversationStatusService.deleteStatus(status.statusId);
          toast.success('Estado eliminado');
          await loadStatuses();
        } catch (error: any) {
          console.error('Error deleting status:', error);
          if (error.response?.data?.hasConversations) {
            toast.error('No se puede eliminar un estado con conversaciones activas');
          } else {
            toast.error(error.response?.data?.error || 'Error al eliminar el estado');
          }
        } finally {
          setLoading(false);
          hideLoader();
          setConfirmDialog({ ...confirmDialog, isOpen: false });
        }
      },
    });
  };

  const handleConversationClick = (conversation: Conversation) => {
    setSelectedConversation(conversation);
    setShowStatusChangeModal(true);
  };

  const handleChangeConversationStatus = async (newStatusId: string) => {
    if (!selectedConversation || !user) return;

    // Cerrar modal inmediatamente — feedback visual instantáneo
    setShowStatusChangeModal(false);
    const previousConversation = selectedConversation;
    setSelectedConversation(null);

    // Actualización optimista: mover el card antes del fetch
    setConversationsByStatus(prev => {
      const updated: Record<string, Conversation[]> = {};
      // Copiar todas las columnas filtrando la conversación de donde sea que esté
      for (const [key, list] of Object.entries(prev)) {
        updated[key] = list.filter(c => c.id !== previousConversation.id);
      }
      // Insertar en la columna destino
      if (!updated[newStatusId]) updated[newStatusId] = [];
      updated[newStatusId] = [...updated[newStatusId], { ...previousConversation, statusId: newStatusId }];
      return updated;
    });

    try {
      showLoader('Actualizando estado...');
      await conversationStatusService.setConversationStatus(
        previousConversation.pageId,
        previousConversation.senderId,
        newStatusId,
        user.userId,
        user.name,
        user.email
      );
      toast.success('Estado actualizado');
    } catch (error: any) {
      console.error('Error changing status:', error);
      toast.error(error.response?.data?.error || 'Error al cambiar el estado');
      // Revertir — quitar de destino y restaurar en origen
      setConversationsByStatus(prev => {
        const updated: Record<string, Conversation[]> = {};
        for (const [key, list] of Object.entries(prev)) {
          updated[key] = list.filter(c => c.id !== previousConversation.id);
        }
        const oldStatusId = previousConversation.statusId || 'no-status';
        if (!updated[oldStatusId]) updated[oldStatusId] = [];
        updated[oldStatusId] = [...updated[oldStatusId], previousConversation];
        return updated;
      });
    } finally {
      hideLoader();
    }
  };

  // Filtrar conversaciones por búsqueda y filtros
  const filterConversations = (conversations: Conversation[]) => {
    let filtered = conversations;

    // Filtro de búsqueda
    if (searchTerm) {
      filtered = filtered.filter(conv => 
        conv.phoneNumber.includes(searchTerm) ||
        (conv.participantName?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false) ||
        conv.lastMessage.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtro de no leídos
    if (filterUnread) {
      filtered = filtered.filter(conv => conv.unreadCount && conv.unreadCount > 0);
    }

    // Filtro de asesor
    if (filterAdvisorId === 'unassigned') {
      filtered = filtered.filter(conv => !conv.assignedAdvisorId);
    } else if (filterAdvisorId !== 'all') {
      filtered = filtered.filter(conv => conv.assignedAdvisorId === filterAdvisorId);
    }

    return filtered;
  };

  // Calcular estadísticas
  const getStatistics = () => {
    const allConversations = Object.values(conversationsByStatus).flat();
    const totalConversations = allConversations.length;
    const unreadConversations = allConversations.filter(c => c.unreadCount && c.unreadCount > 0).length;
    const conversationsWithStatus = allConversations.filter(c => c.statusId).length;
    const conversationsWithoutStatus = allConversations.filter(c => !c.statusId).length;

    return {
      total: totalConversations,
      unread: unreadConversations,
      withStatus: conversationsWithStatus,
      withoutStatus: conversationsWithoutStatus,
    };
  };

  // Exportar a CSV
  const handleExportCSV = () => {
    const allConversations = Object.values(conversationsByStatus).flat();
    const filtered = filterConversations(allConversations);

    // Crear CSV
    const headers = ['Nombre', 'Teléfono', 'Estado', 'Último Mensaje', 'Fecha', 'No Leídos'];
    const rows = filtered.map(conv => {
      const statusName = statuses.find(s => s.statusId === conv.statusId)?.name || 'Sin estado';
      return [
        conv.participantName || 'Sin nombre',
        conv.phoneNumber,
        statusName,
        conv.lastMessage.replace(/,/g, ';'), // Reemplazar comas para no romper CSV
        conv.lastMessageTime,
        conv.unreadCount || 0,
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    // Descargar archivo
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `conversaciones_${selectedCompany?.nombreEmpresa}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('CSV descargado');
  };

  // Drag & Drop handlers
  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    setActiveId(active.id as string);
    
    // Encontrar la conversación que se está arrastrando
    const allConversations = Object.values(conversationsByStatus).flat();
    const conversation = allConversations.find(c => c.id === active.id);
    setDraggedConversation(conversation || null);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    
    setActiveId(null);
    setDraggedConversation(null);

    if (!over || active.id === over.id || !user) return;

    const conversationId = active.id as string;
    const newStatusId = over.id as string;

    // Encontrar la conversación
    const allConversations = Object.values(conversationsByStatus).flat();
    const conversation = allConversations.find(c => c.id === conversationId);
    
    if (!conversation) return;

    // Si ya está en ese estado, no hacer nada
    const currentStatusId = conversation.statusId || 'no-status';
    
    if (currentStatusId === newStatusId) return;

    // Asesor solo puede mover conversaciones que le están asignadas
    if (user.role === 'asesor') {
      const hasAccess = canAccessConversation(conversation.pageId, conversation.senderId);
      if (!hasAccess) {
        toast.error('No tienes permiso para mover esta conversación');
        return;
      }
    }

    // Si se mueve a "no-status", no enviar al backend (no es un estado real)
    if (newStatusId === 'no-status') {
      toast.error('No se puede mover a "Sin Estado". Usa el modal para quitar el estado.');
      return;
    }

    try {
      // Actualización optimista — todo dentro del setter para usar el estado más reciente
      setConversationsByStatus(prev => {
        const updated: Record<string, Conversation[]> = {};
        // Copiar todas las columnas filtrando la conversación de donde sea que esté
        for (const [key, list] of Object.entries(prev)) {
          updated[key] = list.filter(c => c.id !== conversationId);
        }
        // Insertar en la columna destino
        if (!updated[newStatusId]) updated[newStatusId] = [];
        updated[newStatusId] = [...updated[newStatusId], { ...conversation, statusId: newStatusId }];
        return updated;
      });

      showLoader('Moviendo conversación...');

      await conversationStatusService.setConversationStatus(
        conversation.pageId,
        conversation.senderId,
        newStatusId,
        user.userId,
        user.name,
        user.email
      );

      toast.success('Estado actualizado');
    } catch (error: any) {
      console.error('Error changing status:', error);
      toast.error(error.response?.data?.error || 'Error al cambiar el estado');
      // Revertir — quitar de destino y restaurar en origen
      setConversationsByStatus(prev => {
        const updated: Record<string, Conversation[]> = {};
        for (const [key, list] of Object.entries(prev)) {
          updated[key] = list.filter(c => c.id !== conversationId);
        }
        if (!updated[currentStatusId]) updated[currentStatusId] = [];
        updated[currentStatusId] = [...updated[currentStatusId], conversation];
        return updated;
      });
    } finally {
      hideLoader();
    }
  };

  // Ver historial de una conversación
  const handleViewHistory = async (conversation: Conversation) => {
    try {
      setLoadingHistory(true);
      setShowHistoryModal(true);
      const history = await conversationStatusService.getConversationHistory(
        conversation.pageId,
        conversation.senderId
      );
      setHistoryData(history);
    } catch (error) {
      console.error('Error loading history:', error);
      toast.error('Error al cargar el historial');
    } finally {
      setLoadingHistory(false);
    }
  };

  // Ver detalle de conversación (abrir en nueva pestaña o modal)
  const handleViewDetail = (conversation: Conversation, e: React.MouseEvent) => {
    e.stopPropagation();
    // Abrir la página de conversaciones con esta conversación seleccionada
    window.open(`/conversations?pageId=${conversation.pageId}&senderId=${conversation.senderId}`, '_blank');
  };

  // Toggle CRM integration - Solo marca/desmarca, no envía al CRM
  const handleToggleCRM = async (conversation: Conversation, e: React.MouseEvent) => {
    e.stopPropagation();
    if (loadingCRMId) return;
    
    try {
      setLoadingCRMId(conversation.id);
      showLoader(conversation.crmIntegrated ? 'Desmarcando CRM...' : 'Marcando en CRM...');
      await conversationService.markAsCrmIntegrated(conversation.pageId, conversation.senderId);
      
      setConversationsByStatus(prev => {
        const updated = { ...prev };
        const statusId = conversation.statusId || 'no-status';
        if (updated[statusId]) {
          updated[statusId] = updated[statusId].map(conv => 
            conv.id === conversation.id 
              ? { ...conv, crmIntegrated: !conv.crmIntegrated }
              : conv
          );
        }
        return updated;
      });
      
      toast.success(conversation.crmIntegrated ? 'Desmarcado de CRM' : 'Marcado como integrado en CRM');
    } catch (error: any) {
      console.error('Error toggling CRM:', error);
      toast.error('Error al actualizar marca de CRM');
    } finally {
      setLoadingCRMId(null);
      hideLoader();
    }
  };

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div className="flex flex-col h-full bg-brand-bg">
      {/* Header */}
      <div className="bg-brand-surface border-b border-brand-border p-3 md:p-4">
        <div className="flex items-center justify-between mb-3 md:mb-4">
          <h1 className="text-base md:text-2xl font-bold text-brand-dark leading-tight">Tablero</h1>
          <div className="flex gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={handleExportCSV}
              disabled={!selectedCompany || Object.values(conversationsByStatus).flat().length === 0}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="hidden sm:inline">Exportar CSV</span>
            </Button>
            {user?.role !== 'asesor' && (
              <Button variant="primary" size="sm" onClick={() => handleOpenStatusModal()}>
                <span className="hidden sm:inline">+ Nuevo Estado</span>
                <span className="sm:hidden">+ Estado</span>
              </Button>
            )}
          </div>
        </div>

        {/* Statistics — 2 cols en mobile, 4 en desktop */}
        {selectedCompany && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 mb-3 md:mb-4">
            {[
              { label: 'Total', value: getStatistics().total, color: 'text-brand-dark', bg: 'bg-brand-dark', icon: <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg> },
              { label: 'No leídos', value: getStatistics().unread, color: 'text-green-600', bg: 'bg-green-500', icon: <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg> },
              { label: 'Con estado', value: getStatistics().withStatus, color: 'text-brand-mid', bg: 'bg-brand-mid', icon: <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> },
              { label: 'Sin estado', value: getStatistics().withoutStatus, color: 'text-gray-500', bg: 'bg-gray-300', icon: <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> },
            ].map(stat => (
              <div key={stat.label} className="bg-brand-surface border border-brand-border rounded-xl p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-brand-mid font-semibold">{stat.label}</p>
                    <p className={`text-xl font-bold ${stat.color}`}>{stat.value}</p>
                  </div>
                  <div className={`w-8 h-8 ${stat.bg} rounded-full flex items-center justify-center flex-shrink-0`}>
                    {stat.icon}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Company Selector and Filters */}
        <div className="space-y-2">
          {/* Fila 1: empresa + asesores + refresh */}
          <div className="flex items-center gap-2 flex-wrap">
            <CustomSelect
              value={selectedCompany?.configId || ''}
              onChange={(val) => {
                const company = companies.find(c => c.configId === val);
                if (company) setSelectedCompany(company);
              }}
              placeholder="-- Selecciona empresa --"
              className="flex-1 min-w-0 max-w-[200px]"
              options={companies.map(c => ({ value: c.configId, label: c.nombreEmpresa }))}
            />

            {advisors.length > 0 && (
              <CustomSelect
                value={filterAdvisorId}
                onChange={(val) => setFilterAdvisorId(val)}
                className="flex-1 min-w-0 max-w-[180px]"
                options={[
                  { value: 'all', label: 'Todos los asesores' },
                  { value: 'unassigned', label: 'Sin asignar' },
                  ...advisors.map(a => ({ value: a.userId, label: a.name })),
                ]}
              />
            )}

            <button
              onClick={loadConversations}
              disabled={loading}
              title="Actualizar conversaciones"
              className="p-2 hover:bg-brand-light rounded-lg transition disabled:opacity-50 text-brand-mid hover:text-brand-dark flex-shrink-0 ml-auto"
            >
              <svg className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>

          {/* Fila 2: búsqueda + fecha + filtros */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative" style={{ minWidth: '160px', maxWidth: '480px', flex: '1 1 160px' }}>
              <input
                type="text"
                placeholder="Buscar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 pl-9 bg-brand-light border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-yellow text-sm"
              />
              <svg className="w-4 h-4 text-brand-mid absolute left-2.5 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>

            <CustomSelect
              value={datePreset}
              onChange={(val) => {
                const value = val as any;
                setDatePreset(value);
                if (value === 'custom') {
                  const today = new Date();
                  const formatLocalDate = (date: Date) => {
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    return `${year}-${month}-${day}`;
                  };
                  const todayFormatted = formatLocalDate(today);
                  setStartDate(todayFormatted);
                  setEndDate(todayFormatted);
                } else {
                  setStartDate('');
                  setEndDate('');
                }
              }}
              className="w-auto"
              options={[
                { value: 'all', label: 'Todas las fechas' },
                { value: 'today', label: 'Hoy' },
                { value: 'week', label: 'Última semana' },
                { value: 'month', label: 'Último mes' },
                { value: 'custom', label: 'Rango personalizado' },
              ]}
            />

            {datePreset === 'custom' && (
              <>
                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                  className="px-3 py-2 bg-brand-light border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-yellow text-sm" />
                <span className="text-brand-mid text-xs">—</span>
                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                  className="px-3 py-2 bg-brand-light border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-yellow text-sm" />
              </>
            )}
          </div>

          {/* Fila 3: toggle leídos + limpiar */}
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 bg-brand-light rounded-xl p-1 flex-shrink-0">
              <button
                onClick={() => setFilterUnread(false)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition select-none cursor-pointer ${
                  !filterUnread ? 'bg-brand-surface text-brand-dark shadow-sm' : 'text-brand-mid hover:text-brand-dark'
                }`}
              >
                Todo
              </button>
              <button
                onClick={() => setFilterUnread(true)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition flex items-center gap-1.5 select-none cursor-pointer ${
                  filterUnread ? 'bg-brand-surface text-brand-dark shadow-sm' : 'text-brand-mid hover:text-brand-dark'
                }`}
              >
                No leídos
                {Object.values(conversationsByStatus).flat().filter(c => (c.unreadCount ?? 0) > 0).length > 0 && (
                  <span className={`pointer-events-none text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center flex-shrink-0 ${
                    filterUnread ? 'bg-brand-yellow text-brand-dark' : 'bg-brand-mid/20 text-brand-mid'
                  }`}>
                    {Object.values(conversationsByStatus).flat().filter(c => (c.unreadCount ?? 0) > 0).length}
                  </span>
                )}
              </button>
            </div>

            {(() => {
              const hasFilters = searchTerm || filterUnread || filterAdvisorId !== 'all' || datePreset !== 'today' || dateField !== 'updatedAt';
              return (
                <Button variant="secondary" size="sm" disabled={!hasFilters}
                  onClick={() => { setSearchTerm(''); setFilterUnread(false); setFilterAdvisorId('all'); setDatePreset('today'); setDateField('updatedAt'); setStartDate(''); setEndDate(''); }}>
                  Limpiar
                </Button>
              );
            })()}

            <Button variant="primary" size="sm" onClick={() => loadConversations()} loading={loading} disabled={loading}>
              {loading ? 'Buscando...' : 'Buscar'}
            </Button>
          </div>
        </div>
      </div>

      {/* Kanban Board */}
      <div className="flex-1 overflow-x-auto p-4">
        <div className="flex gap-4 h-full min-w-max">
          {/* Sin estado - SIEMPRE PRIMERO */}
          {conversationsByStatus['no-status'] && conversationsByStatus['no-status'].length > 0 && (
            <DroppableColumn id="no-status" className="flex flex-col w-80 bg-brand-surface rounded-2xl border border-brand-border">
              <div className="p-4 bg-brand-mid/30 rounded-t-2xl flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <h3 className="font-bold text-brand-dark">Sin Estado</h3>
                  <span className="bg-brand-mid/20 text-brand-dark text-xs font-bold px-2 py-1 rounded-full">
                    {filterConversations(conversationsByStatus['no-status'] || []).length}
                  </span>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-2 space-y-2">
                {filterConversations(conversationsByStatus['no-status'] || []).map((conversation) => (
                  <DraggableConversationCard
                    key={conversation.id}
                    conversation={conversation}
                    onClick={() => handleConversationClick(conversation)}
                    onViewDetail={(e) => handleViewDetail(conversation, e)}
                    onToggleCRM={(e) => handleToggleCRM(conversation, e)}
                    onViewHistory={(e) => {
                      e.stopPropagation();
                      handleViewHistory(conversation);
                    }}
                  />
                ))}
                {filterConversations(conversationsByStatus['no-status'] || []).length === 0 && (
                  <div className="text-center text-gray-400 text-sm py-8">
                    {searchTerm || filterUnread ? 'Sin resultados' : 'Sin conversaciones'}
                  </div>
                )}
              </div>
            </DroppableColumn>
          )}
          
          {/* Estados configurados */}
          {statuses.map((status) => (
            <DroppableColumn key={status.statusId} id={status.statusId} className="flex flex-col w-80 bg-brand-surface rounded-2xl border border-brand-border">
              {/* Column Header */}
              <div 
                className="p-4 rounded-t-2xl flex items-center justify-between"
                style={{ backgroundColor: status.color }}
              >
                <div className="flex items-center gap-2">
                  <h3 className="font-bold text-white">{status.name}</h3>
                  <span className="bg-white/30 text-white text-xs font-bold px-2 py-1 rounded-full">
                    {filterConversations(conversationsByStatus[status.statusId] || []).length}
                  </span>
                </div>
                {/* Solo administradores y operadores pueden editar/eliminar estados */}
                {user?.role !== 'asesor' && (
                  <div className="relative">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenMenuStatusId(openMenuStatusId === status.statusId ? null : status.statusId);
                      }}
                      className="p-1 hover:bg-white/20 rounded transition"
                    >
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                      </svg>
                    </button>
                    
                    {/* Dropdown Menu */}
                    {openMenuStatusId === status.statusId && (
                      <div 
                        className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl z-10 border border-gray-200"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          onClick={() => {
                            handleOpenStatusModal(status);
                            setOpenMenuStatusId(null);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-t-lg flex items-center gap-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          Editar
                        </button>
                        <button
                          onClick={() => {
                            handleDeleteStatus(status);
                            setOpenMenuStatusId(null);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-b-lg flex items-center gap-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          Eliminar
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Cards */}
              <div className="flex-1 overflow-y-auto p-2 space-y-2">
                {filterConversations(conversationsByStatus[status.statusId] || []).map((conversation) => (
                  <DraggableConversationCard
                    key={conversation.id}
                    conversation={conversation}
                    onClick={() => handleConversationClick(conversation)}
                    onViewDetail={(e) => handleViewDetail(conversation, e)}
                    onToggleCRM={(e) => handleToggleCRM(conversation, e)}
                    onViewHistory={(e) => {
                      e.stopPropagation();
                      handleViewHistory(conversation);
                    }}
                  />
                ))}
                {filterConversations(conversationsByStatus[status.statusId] || []).length === 0 && (
                  <div className="text-center text-gray-400 text-sm py-8">
                    {searchTerm || filterUnread ? 'Sin resultados' : 'Sin conversaciones'}
                  </div>
                )}
              </div>
            </DroppableColumn>
          ))}
        </div>
      </div>

      {/* Drag Overlay */}
      <DragOverlay>
        {draggedConversation && (
          <div className="bg-white p-3 rounded-2xl shadow-xl border-2 border-brand-yellow opacity-90 w-80">
            <div className="flex items-start gap-2 mb-2">
              <div className="w-10 h-10 rounded-full bg-brand-dark flex items-center justify-center text-brand-yellow font-bold flex-shrink-0">
                {(draggedConversation.participantName || draggedConversation.phoneNumber).charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 truncate">
                  {draggedConversation.participantName || 'Sin nombre'}
                </p>
                <p className="text-xs text-gray-500">{draggedConversation.phoneNumber}</p>
              </div>
            </div>
            <p className="text-sm text-gray-600 line-clamp-2">
              {draggedConversation.lastMessage}
            </p>
          </div>
        )}
      </DragOverlay>

      {/* Status Modal - TODO: Implementar en siguiente iteración */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-brand-surface rounded-2xl w-full max-w-md">
            {/* Header */}
            <div className="bg-brand-dark text-white p-6 rounded-t-2xl">
              <h2 className="text-xl font-bold">
                {editingStatus ? 'Editar Estado' : 'Nuevo Estado'}
              </h2>
              <p className="text-white/70 text-sm mt-1">
                {editingStatus ? 'Modifica las propiedades del estado' : 'Crea un nuevo estado para organizar conversaciones'}
              </p>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              {/* Error Message */}
              {modalErrorMessage && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {modalErrorMessage}
                </div>
              )}

              {/* Nombre */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Nombre del Estado *
                </label>
                <input
                  type="text"
                  value={statusForm.name}
                  onChange={(e) => setStatusForm({ ...statusForm, name: e.target.value })}
                  placeholder="Ej: En progreso, Contactado, Finalizado"
                  className="w-full px-4 py-2 bg-brand-light border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-yellow"
                  maxLength={50}
                />
              </div>

              {/* Color */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Color
                </label>
                
                {/* Colores predefinidos */}
                <div className="grid grid-cols-5 gap-2 mb-3">
                  {predefinedColors.map((color) => (
                    <button
                      key={color}
                      onClick={() => setStatusForm({ ...statusForm, color })}
                      className={`w-full h-10 rounded-lg transition ${
                        statusForm.color === color
                          ? 'ring-2 ring-offset-2 ring-brand-yellow scale-110'
                          : 'hover:scale-105'
                      }`}
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                  ))}
                </div>

                {/* Selector de color personalizado */}
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={statusForm.color}
                    onChange={(e) => setStatusForm({ ...statusForm, color: e.target.value })}
                    className="w-16 h-10 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={statusForm.color}
                    onChange={(e) => setStatusForm({ ...statusForm, color: e.target.value })}
                    placeholder="#3B82F6"
                    className="flex-1 px-3 py-2 bg-brand-light border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-yellow font-mono text-sm"
                    maxLength={7}
                  />
                </div>
              </div>

              {/* Vista previa */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Vista Previa
                </label>
                <div 
                  className="p-4 rounded-lg flex items-center justify-between"
                  style={{ backgroundColor: statusForm.color }}
                >
                  <span className="font-bold text-white">
                    {statusForm.name || 'Nombre del estado'}
                  </span>
                  <span className="bg-white/30 text-white text-xs font-bold px-2 py-1 rounded-full">
                    0
                  </span>
                </div>
              </div>

              {/* Estado por defecto */}
              <div className="flex items-center gap-3 p-3 bg-brand-light rounded-xl">
                <input
                  type="checkbox"
                  id="isDefault"
                  checked={statusForm.isDefault}
                  onChange={(e) => setStatusForm({ ...statusForm, isDefault: e.target.checked })}
                  className="w-4 h-4 text-brand-dark rounded focus:ring-2 focus:ring-brand-yellow"
                />
                <label htmlFor="isDefault" className="text-sm text-gray-700 cursor-pointer">
                  Establecer como estado por defecto para nuevas conversaciones
                </label>
              </div>
            </div>

            {/* Actions */}
            <div className="border-t border-gray-100 p-6 flex gap-3 justify-end bg-brand-light rounded-b-2xl">
              {editingStatus && (
                <Button
                  variant="danger"
                  className="mr-auto"
                  onClick={() => handleDeleteStatus(editingStatus)}
                  disabled={loading}
                >
                  Eliminar
                </Button>
              )}
              <Button
                variant="secondary"
                onClick={handleCloseStatusModal}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={handleSaveStatus}
                loading={loading}
                disabled={loading || !statusForm.name.trim()}
              >
                {loading ? 'Guardando...' : editingStatus ? 'Actualizar' : 'Crear'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Success/Error Toast - removed, using global toast */}

      {/* Confirm Dialog */}
      {confirmDialog.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-brand-surface rounded-2xl w-full max-w-md">
            <div className="p-6">
              <h3 className="text-lg font-bold text-brand-dark mb-2">{confirmDialog.title}</h3>
              <p className="text-gray-600 mb-6">{confirmDialog.message}</p>
              <div className="flex gap-3 justify-end">
                <Button
                  variant="secondary"
                  onClick={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
                  disabled={loading}
                >
                  Cancelar
                </Button>
                <Button
                  variant="danger"
                  onClick={confirmDialog.onConfirm}
                  loading={loading}
                  disabled={loading}
                >
                  {loading ? 'Eliminando...' : 'Eliminar'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status Change Modal */}
      {showStatusChangeModal && selectedConversation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-brand-surface rounded-2xl w-full max-w-md">
            {/* Header */}
            <div className="bg-brand-dark text-white p-6 rounded-t-2xl">
              <h2 className="text-xl font-bold">Cambiar Estado</h2>
              <p className="text-white/70 text-sm mt-1">
                {selectedConversation.participantName || selectedConversation.phoneNumber}
              </p>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">
                Selecciona el nuevo estado para esta conversación:
              </p>
              
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {statuses.map((status) => {
                  const isCurrentStatus = selectedConversation.statusId === status.statusId;
                  return (
                    <button
                      key={status.statusId}
                      onClick={() => !isCurrentStatus && handleChangeConversationStatus(status.statusId)}
                      disabled={loading || isCurrentStatus}
                      className={`w-full p-4 rounded-lg text-left transition ${
                        isCurrentStatus
                          ? 'opacity-50 cursor-not-allowed'
                          : 'hover:shadow-md cursor-pointer'
                      }`}
                      style={{ 
                        backgroundColor: status.color,
                        opacity: isCurrentStatus ? 0.5 : 1,
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-white">{status.name}</span>
                        {isCurrentStatus && (
                          <span className="text-xs text-white bg-white/30 px-2 py-1 rounded-full">
                            Actual
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Actions */}
            <div className="border-t border-gray-100 p-6 flex gap-3 justify-between bg-brand-light rounded-b-2xl">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowStatusChangeModal(false);
                  handleViewHistory(selectedConversation);
                }}
                disabled={loading}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Ver Historial
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowStatusChangeModal(false);
                  setSelectedConversation(null);
                }}
                disabled={loading}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {showHistoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-brand-surface rounded-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="bg-brand-dark text-white p-6 rounded-t-2xl">
              <h2 className="text-xl font-bold">Historial de Cambios</h2>
              <p className="text-white/70 text-sm mt-1">
                Registro de todos los cambios de estado
              </p>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {loadingHistory ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-dark"></div>
                </div>
              ) : historyData.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p>No hay historial de cambios</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {historyData.map((entry, index) => (
                    <div key={entry.historyId} className="relative">
                      {/* Timeline line */}
                      {index < historyData.length - 1 && (
                        <div className="absolute left-6 top-12 bottom-0 w-0.5 bg-gray-200"></div>
                      )}
                      
                      <div className="flex gap-4">
                        {/* Timeline dot */}
                        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-brand-light flex items-center justify-center border-4 border-white shadow">
                          <svg className="w-6 h-6 text-brand-mid" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                        </div>

                        {/* Content */}
                        <div className="flex-1 bg-brand-light rounded-lg p-4 border border-brand-border">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <p className="font-semibold text-gray-900">
                                {entry.previousStatusName ? (
                                  <>
                                    <span className="text-red-600">{entry.previousStatusName}</span>
                                    {' → '}
                                    <span className="text-green-600">{entry.newStatusName}</span>
                                  </>
                                ) : (
                                  <>
                                    Estado asignado: <span className="text-green-600">{entry.newStatusName}</span>
                                  </>
                                )}
                              </p>
                              <p className="text-sm text-gray-600 mt-1">
                                Por: <span className="font-medium">{entry.changedByName}</span>
                                {entry.changedByEmail && (
                                  <span className="text-gray-500"> ({entry.changedByEmail})</span>
                                )}
                              </p>
                            </div>
                            <span className="text-xs text-gray-500 whitespace-nowrap">
                              {new Date(entry.timestamp).toLocaleString('es-ES', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="border-t border-gray-100 p-6 flex gap-3 justify-end bg-brand-light rounded-b-2xl">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowHistoryModal(false);
                  setHistoryData([]);
                }}
              >
                Cerrar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
    </DndContext>
  );
};

export default ConversationBoardPage;
